import React, { useId } from 'react';
import ReactSelect, {
  type ActionMeta,
  type ClearIndicatorProps,
  components,
  type DropdownIndicatorProps,
  type GroupBase,
  type MenuPlacement,
  type OnChangeValue,
  type SingleValueProps,
  type StylesConfig,
} from 'react-select';
import './select.css';
import { Icons } from '../../icons/Icon';

type Option = {
  value: number | string;
  label: string;
  options?: never;
};

type Props = {
  options?: Array<Option>;
  value?: number | string | null;
  onChange?: (value: string | null) => void;
  isMulti?: boolean;
  isLoading?: boolean;
  borderColor?: string;
  placeholder?: string;
  isDisabled?: boolean;
  minHeight?: number;
  size?: number | string;
  IconStyle?: React.CSSProperties;
  isRtl?: boolean;
  menuPlacement?: MenuPlacement;
  isClearable?: boolean;
  width?: number;
  theme?: 'light' | 'dark';
  singleValueIcon?: string;
  caret?: boolean;
  textAlign?: 'start' | 'center' | 'end';
  dataTestId?: string;
};

export const Select = ({
  options = [],
  value,
  onChange,
  isMulti = false,
  isLoading = false,
  borderColor = '',
  isDisabled = false,
  minHeight,
  size = 180,
  IconStyle = {},
  isRtl = false,
  placeholder = '',
  menuPlacement = 'auto',
  isClearable = true,
  caret = true,
  textAlign = 'start',
  // Интегрировать в SaigonUIThemeProvider
  theme = 'light',
  singleValueIcon,
  dataTestId,
}: Props) => {
  const THEMES: Record<
    'light' | 'dark',
    {
      backgroundColor: string;
      backgroundSelected: string;
      opacity: number;
      optionColor: string;
      color: string;
      selected: string;
      borderColor: string;
      hoverColor?: string;
      hoverBackground?: string;
    }
  > = {
    light: {
      backgroundColor: isDisabled ? '#e5e4e4' : '#fff',
      backgroundSelected: '#4e6188',
      opacity: 1,
      optionColor: '#727272',
      color: 'var(--primary-color-300)',
      selected: '#fff',
      borderColor: '#000',
      hoverColor: 'black',
      hoverBackground: '#ccc',
    },
    dark: {
      backgroundColor: 'black',
      backgroundSelected: 'transparent',
      opacity: 0.8,
      optionColor: '#fff',
      color: '#fff',
      borderColor: '#fff',
      selected: '#fff',
    },
  };

  const id = useId();
  const DropdownIndicator = (props: DropdownIndicatorProps<Option, boolean, GroupBase<Option>>) => {
    const menuIsOpen = props.selectProps.menuIsOpen;
    return (
      <components.DropdownIndicator {...props}>
        {menuIsOpen ? (
          <div id="open-icon">
            {' '}
            <Icons icon="ChevronUp" size={14} customStyle={IconStyle} />
          </div>
        ) : (
          <div id="close-icon">
            <Icons icon="ChevronDown" size={14} customStyle={IconStyle} />
          </div>
        )}
      </components.DropdownIndicator>
    );
  };

  const ClearIndicator = (props: ClearIndicatorProps<Option, boolean, GroupBase<Option>>) => {
    return (
      <components.ClearIndicator {...props}>
        <Icons icon="Xmark" size={14} customStyle={IconStyle} />
      </components.ClearIndicator>
    );
  };

  const SingleValue = (props: SingleValueProps<Option, boolean, GroupBase<Option>>) => {
    return (
      <components.SingleValue {...props}>
        <div
          style={{
            display: 'flex',
            gap: 5,
            alignItems: 'center',
            color: 'inherit',
          }}
        >
          {singleValueIcon ? <Icons icon={singleValueIcon} size={14} customStyle={IconStyle} /> : null}
          {props.children}
        </div>
      </components.SingleValue>
    );
  };

  const getValue = () => {
    if (isMulti) {
      return options?.filter((i) => value?.includes(i.value)) || [];
    }
    return options.find((item) => item.value == value) || null;
  };
  // TODO Поправить логику в случае с несуществующим выбранным значением
  const handleChange = (option: Option | null) => {
    if (!option?.value) {
      return onChange?.(null);
    }
    onChange?.(String(option.value));
  };

  const _borderColor = isDisabled ? '#aaaaaa' : borderColor ? borderColor : THEMES[theme].borderColor;

  const colourStyles: StylesConfig<Option, boolean> = {
    control: (baseStyles) => ({
      ...baseStyles,
      borderColor: `${_borderColor} !important`,
      minHeight: minHeight ? minHeight : '28px',
      backgroundColor: THEMES[theme].backgroundColor,
      cursor: 'pointer',
      color: THEMES[theme].color,
      width: Number.isNaN(+size) ? `${size}` : `${size}px !important`,
      caretColor: !caret ? 'transparent' : 'initial',
    }),

    menu: (baseStyles) => ({
      ...baseStyles,
      color: THEMES[theme].color,
      opacity: THEMES[theme].opacity,
      width: Number.isNaN(+size) ? `${size}` : `${size}px`,
      backgroundColor: THEMES[theme].backgroundColor,
      textAlign: textAlign,
    }),
    menuPortal: (baseStyles) => ({
      ...baseStyles,
      width: Number.isNaN(+size) ? `${size}` : `${size}px`,
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      color: state.isSelected ? THEMES[theme].selected : THEMES[theme].optionColor,
      backgroundColor: state.isSelected ? THEMES[theme].backgroundSelected : THEMES[theme].backgroundColor,
      cursor: 'pointer',
      width: Number.isNaN(+size) ? `${size}` : `${size}px`,
      '&:hover': {
        color: THEMES[theme].hoverColor ?? baseStyles.color,
        background: THEMES[theme].hoverBackground ?? baseStyles.backgroundColor,
      },
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: THEMES[theme].color,
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      color: THEMES[theme].color,
    }),
  };

  // Извлекаем options и оставляем остальные свойства так как ReactSelect пытается из них сделать вложенные группы
  const flatOptions = React.useMemo(() => {
    return options.map((option) => {
      const { options: _options, ...rest } = option;
      return rest;
    });
  }, [options]);

  return (
    <div data-testid={dataTestId} id={'saigon-ui-select'}>
      <input type="hidden" data-testid="select-value" name={`select-${id}-value`} value={getValue()?.value ?? ''} />
      <ReactSelect
        instanceId={id}
        isRtl={isRtl}
        isClearable={isClearable}
        classNamePrefix={`react__select_input`}
        styles={colourStyles}
        options={flatOptions}
        unstyled={true}
        value={getValue()}
        isLoading={isLoading}
        onChange={(newValue: OnChangeValue<Option, boolean>, _meta: ActionMeta<Option>) => {
          if (isMulti) {
            return onChange(newValue.map((v) => v.value));
          }
          const option = Array.isArray(newValue) ? (newValue[0] ?? null) : (newValue as Option | null);
          handleChange(option);
        }}
        isMulti={isMulti}
        isDisabled={isDisabled}
        placeholder={placeholder}
        menuPlacement={menuPlacement}
        menuShouldScrollIntoView={false}
        components={{ DropdownIndicator, ClearIndicator, SingleValue }}
      />
    </div>
  );
};
export default Select;
